/**
 * Authentication Service
 * 
 * Handles:
 * - Phone + OTP login
 * - JWT token management
 * - Token refresh
 * - Session persistence
 */

import { authApi } from './api';
import { useAuthStore } from '@/store/auth';
import { TOKEN_KEY, REFRESH_TOKEN_KEY } from './constants';
import { logger } from './logger';

export class AuthService {
  /**
   * Request OTP for phone number
   */
  async requestOTP(phone: string): Promise<void> {
    try {
      logger.info('Requesting OTP', { phone });
      await authApi.requestOTP(phone);
      logger.info('OTP sent successfully');
    } catch (error) {
      logger.error('OTP request failed', error as Error, { phone });
      throw error;
    }
  }

  /**
   * Verify OTP and login
   */
  async verifyOTP(phone: string, code: string): Promise<void> {
    try {
      logger.info('Verifying OTP', { phone });
      
      const response = await authApi.verifyOTP(phone, code);

      if (!response.success || !response.data) {
        throw new Error('Invalid response from server');
      }

      const { access_token, user } = response.data;

      // Store token (no refresh token in current implementation)
      localStorage.setItem(TOKEN_KEY, access_token);

      // Map user to expected format
      const mappedUser = {
        id: user.id,
        phone: user.phone,
        name: user.name || 'Rider',
        role: user.role, // ✅ Use actual role from backend
      };

      // ⚠️ SECURITY: Verify user has rider role
      if (user.role !== 'rider') {
        logger.error('Non-rider attempting to login to rider app', new Error(`Role mismatch: ${user.role}`));
        throw new Error(`This app is for riders only. Your account role is: ${user.role}`);
      }

      // Update auth store (no refresh token yet) - assert role is 'rider' since we verified above
      useAuthStore.getState().setAuth({ ...mappedUser, role: 'rider' as const }, access_token, '');

      logger.info('Login successful', { userId: user.id });
    } catch (error) {
      logger.error('OTP verification failed', error as Error, { phone });
      throw error;
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      logger.info('Refreshing token');
      
      const { token: newToken, refreshToken: newRefreshToken } = await authApi.refreshToken(refreshToken);

      // Update tokens
      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

      // Update store
      const user = useAuthStore.getState().user;
      if (user) {
        useAuthStore.getState().setAuth(user, newToken, newRefreshToken);
      }

      logger.info('Token refreshed successfully');
    } catch (error) {
      logger.error('Token refresh failed', error as Error);
      throw error;
    }
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    try {
      logger.info('Logging out');
      
      // Call logout API
      await authApi.logout();

      // Clear tokens
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);

      // Clear auth store
      useAuthStore.getState().logout();

      logger.info('Logout successful');
    } catch (error) {
      logger.error('Logout failed', error as Error);
      
      // Clear local state anyway
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      useAuthStore.getState().logout();
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(TOKEN_KEY);
    const authState = useAuthStore.getState();
    return !!token && authState.isAuthenticated;
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return useAuthStore.getState().user;
  }

  /**
   * Initialize auth from storage
   */
  initializeAuth(): void {
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (token && refreshToken) {
      logger.info('Initializing auth from storage');
      // Token exists, but we need to validate it
      // The API interceptor will handle token refresh if needed
    }
  }
}

export const authService = new AuthService();








